import { useEffect, useState } from "react";
import axios from "axios";
import { MapContainer, TileLayer, CircleMarker, Popup } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Map as MapIcon, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Interactive map of registered users per city (bubble size = signups). */
const REFRESH_MS = 60_000;

export default function AdminUsersMap() {
  const [data, setData] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [live, setLive] = useState(true);
  const load = () => axios.get(`${API}/admin/users/geo-map`).then((r) => { setData(r.data); setUpdatedAt(new Date()); }).catch(() => setData((d) => d || { points: [] }));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!live) return;
    const iv = setInterval(() => { if (document.visibilityState === "visible") load(); }, REFRESH_MS);
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { clearInterval(iv); document.removeEventListener("visibilitychange", onVis); };
  }, [live]);
  const points = data?.points || [];
  const max = points.reduce((m, p) => Math.max(m, p.count), 1);
  const center = points.length ? [points[0].lat, points[0].lng] : [-14.235, -51.925];
  return (
    <div className="space-y-2" data-testid="admin-users-map">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1"><MapIcon className="w-3 h-3" /> Mapa de usuários {data ? `· ${data.located_cities} cidade${data.located_cities === 1 ? "" : "s"} localizada${data.located_cities === 1 ? "" : "s"}` : "· carregando..."}</span>
        <button type="button" onClick={() => setLive((v) => !v)} data-testid="admin-users-map-live" className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border normal-case tracking-normal ${live ? "border-emerald-500/40 text-emerald-600 bg-emerald-500/10" : "border-border text-muted-foreground"}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-emerald-500 animate-pulse" : "bg-muted-foreground"}`} /> {live ? "Ao vivo · atualiza a cada 1 min" : "Pausado"}
        </button>
        {updatedAt && <span className="normal-case tracking-normal" data-testid="admin-users-map-updated">Atualizado {updatedAt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}</span>}
        <button type="button" onClick={load} data-testid="admin-users-map-refresh" className="inline-flex items-center gap-1 normal-case tracking-normal hover:text-foreground"><RefreshCw className="w-3 h-3" /> Atualizar</button>
      </div>
      <div className="rounded-xl overflow-hidden border border-border h-72 relative z-0">
        <MapContainer center={center} zoom={points.length ? 5 : 4} scrollWheelZoom={false} className="h-full w-full">
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
          {points.map((p) => (
            <CircleMarker
              key={`${p.city}-${p.state}`}
              center={[p.lat, p.lng]}
              radius={8 + (p.count / max) * 22}
              pathOptions={{ color: "#f97316", fillColor: "#f97316", fillOpacity: 0.45, weight: 2 }}
            >
              <Popup>
                <div className="text-sm font-semibold">{p.city}{p.state ? `, ${p.state}` : ""}</div>
                <div className="text-xs">{p.country} · <b>{p.count}</b> cadastro{p.count === 1 ? "" : "s"}</div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
        {data && points.length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/70 text-sm text-muted-foreground" data-testid="admin-users-map-empty">Nenhum usuário com cidade informada ainda.</div>
        )}
      </div>
    </div>
  );
}
